'use client'

import { useCallback, useSyncExternalStore } from 'react'

// Shared add-to-comparison state over the SAME localStorage key the directory
// persists ('ht-comparisons', a JSON array of release slugs) — so a selection
// made on a brand page carries into /hearing-aids/compare and vice versa.
//
// The directory keeps its own reducer-based path for now (no regression risk);
// this hook exists for surfaces outside the directory (brand-page modules).
// Multiple islands on one page stay in sync via a window custom event; the
// native 'storage' event covers other tabs. The server snapshot is empty —
// cards hydrate unchecked and flip once the client reads storage.

const STORAGE_KEY = 'ht-comparisons'
const CHANGE_EVENT = 'ht-comparisons-changed'

export const MAX_COMPARE = 4

const EMPTY: ReadonlySet<string> = new Set()

let snapshot: ReadonlySet<string> = EMPTY
let snapshotRaw: string | null = null

const read = (): ReadonlySet<string> => {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    raw = null
  }
  if (raw === snapshotRaw) {
    return snapshot
  }
  snapshotRaw = raw
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : []
    snapshot = new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [])
  } catch {
    snapshot = new Set()
  }
  return snapshot
}

const write = (next: Set<string>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]))
  } catch {
    // Storage unavailable (private mode) — the in-memory snapshot still works
    // for this page view.
    snapshotRaw = null
    snapshot = next
  }
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGE_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}

const getServerSnapshot = (): ReadonlySet<string> => EMPTY

export const useCompared = () => {
  const compared = useSyncExternalStore(subscribe, read, getServerSnapshot)

  const toggle = useCallback((slug: string) => {
    const next = new Set(read())
    if (next.has(slug)) {
      next.delete(slug)
    } else if (next.size < MAX_COMPARE) {
      next.add(slug)
    }
    write(next)
  }, [])

  const isCompared = useCallback((slug: string) => compared.has(slug), [compared])

  return { compared, isCompared, toggle, atMax: compared.size >= MAX_COMPARE }
}
